import type { Provider } from '@angular/core';
import { inject, Injectable, provideZonelessChangeDetection } from '@angular/core';
import { createApplication } from '@angular/platform-browser';

import { AUTH_TOKEN } from '@core/common-legacy';
import { GenericAuthService } from '@core/http-legacy';
import type { PhraseAppTranslations, SupportedTranslations } from '@core/i18n-legacy';
import { LOCALE, PHRASE_APP_TRANSLATIONS, phraseAppTranslationsLoader } from '@core/i18n-legacy';
import { tellentReportsTranslations } from '@core/reports-data';
import { resolveMemberOrgGuid } from '@core/tellent-data';
import { injectDevFlagSignal } from '@core/tellent-dev-flags-logic';
import { tellentClientAppTranslations } from '@core/tellent-i18n-legacy/locales';
import { provideTellentIsOneSettingsEnabled } from '@core/tellent-logic';
import {
  APP_GUID,
  AppKind,
  AppLanguages,
  BRAND,
  ORG_GUID,
  provideTellentAppKind,
  TELLENT_INITIAL_CURRENT_USER,
  TellentAuthTokenResponse,
  TellentBrand,
  TellentClientConfig,
  TellentClientInitData,
  TellentCurrentUser,
  TellentDevFlag,
  TellentUserResponse,
} from '@core/tellent-types';
import { appTranslations } from '@core/translations-webapp-legacy';
import { uiTranslations } from '@core/user-interface-legacy/locales';
import { provideWalkthroughRegister } from '@core/walkthrough-contracts';

import { tellentClientAppConfig } from './app.config';
import { wysiwygTranslations } from './locales/wysiwyg';
import { WalkthroughStubService } from './services/walkthrough-stub.service';

const AppKindToBrandMap: Record<AppKind, TellentBrand> = {
  [AppKind.Javelo]: TellentBrand.Javelo,
  [AppKind.KiwiHr]: TellentBrand.Kiwi,
  [AppKind.Recruitee]: TellentBrand.Recruitee,
  [AppKind.TellentAdmin]: TellentBrand.Tellent,
};

const BrandToAppKindMap = Object.fromEntries(
  Object.entries(AppKindToBrandMap).map(([k, v]: [AppKind, TellentBrand]) => [v, k]),
);

const AppKindValToAppKindKeyMap = {
  [`${AppKind.Javelo}`]: AppKind.Javelo,
  [`${AppKind.KiwiHr}`]: AppKind.KiwiHr,
  [`${AppKind.Recruitee}`]: AppKind.Recruitee,
  [`${AppKind.TellentAdmin}`]: AppKind.TellentAdmin,
};

const AppKindLocaleKeyMap = {
  [AppKind.KiwiHr]: 'coreHr',
  [AppKind.Recruitee]: 'recruitment',
  [AppKind.Javelo]: 'javelo',
  [AppKind.TellentAdmin]: null,
} satisfies Record<AppKind, keyof AppLanguages | null>;

@Injectable()
export class TellentClientBootstrap {
  private authService: GenericAuthService<TellentAuthTokenResponse, TellentUserResponse> =
    inject(GenericAuthService);

  public async createApplication(
    appProviders: any[],
    initConfig: TellentClientConfig,
    credentials: Partial<TellentClientInitData>,
    user?: TellentCurrentUser,
  ) {
    const userProviders = await this.userProviders(initConfig, credentials, user);
    const appRef = await createApplication({
      providers: [...appProviders, ...userProviders, provideZonelessChangeDetection()],
    });

    return { appRef };
  }

  private loadTranslations(locale: SupportedTranslations) {
    return phraseAppTranslationsLoader(locale, 'en', [
      uiTranslations,
      tellentClientAppTranslations,
      tellentReportsTranslations,
      wysiwygTranslations,
      appTranslations,
    ]);
  }

  private preloadTranslations(locale: SupportedTranslations): Promise<PhraseAppTranslations[]> {
    return this.loadTranslations(locale || 'en');
  }

  private async userProviders(
    initConfig: TellentClientConfig,
    credentials: Partial<TellentClientInitData>,
    _user?: TellentCurrentUser,
  ): Promise<Provider[]> {
    const { user } = _user
      ? { user: _user }
      : await this.authService.fetchUser(
          credentials.accessToken!,
          tellentClientAppConfig(initConfig).tellentUserUrl,
        );

    const userWithCurrentOrg = this.resolveUserWithCurrentOrg(user, credentials);

    const brand =
      'appKind' in credentials ? AppKindToBrandMap[credentials.appKind!] : initConfig.brand;

    const appKindValue =
      ('appKind' in credentials && credentials.appKind
        ? AppKindValToAppKindKeyMap[credentials.appKind]
        : null) ?? BrandToAppKindMap[brand];
    const localeKey = AppKindLocaleKeyMap[appKindValue];
    const locale =
      (localeKey && user.appLanguages ? user.appLanguages[localeKey] : null) ?? user.locale ?? 'en';
    const translations = await this.preloadTranslations(locale);

    return [
      {
        provide: BRAND,
        useValue: brand,
      },
      { provide: LOCALE, useValue: locale },
      { provide: PHRASE_APP_TRANSLATIONS, useValue: translations },
      { provide: AUTH_TOKEN, useValue: credentials.accessToken },
      { provide: APP_GUID, useValue: credentials.appGuid },
      { provide: TELLENT_INITIAL_CURRENT_USER, useValue: userWithCurrentOrg || {} },
      { provide: ORG_GUID, useValue: userWithCurrentOrg.currentOrgGuid },
      provideWalkthroughRegister({ useExisting: WalkthroughStubService }),
      provideTellentAppKind({
        useValue: appKindValue,
      }),
      provideTellentIsOneSettingsEnabled({
        useFactory: () => !!injectDevFlagSignal(TellentDevFlag.OneSettings)(),
      }),
    ];
  }

  private resolveUserWithCurrentOrg(
    user: TellentCurrentUser,
    credentials: Partial<TellentClientInitData>,
  ) {
    if (credentials.appGuid) {
      return {
        ...user,
        currentOrgGuid: resolveMemberOrgGuid(user, credentials.appGuid),
      };
    }

    if ('orgGuid' in credentials) {
      return {
        ...user,
        currentOrgGuid: credentials.orgGuid,
      };
    }

    return {
      ...user,
      currentOrgGuid: undefined,
    };
  }
}
